import "@xyflow/react/dist/base.css";
import "@/modules/workflow/styles/reactflow.css";

import type { FC } from "react";
import { useContext, useEffect } from "react";
import type { GridCard } from "widgets";
import { Card, CardRowSimple, formatDateTimeFromISO } from "widgets";
import { useVisuallyLoading } from "widgets/hooks";

import { useError } from "@/app-utils/graphql/useError.ts";
import type { User } from "@/graphql.ts";
import { getUserDisplayName } from "@/modules/users/utils/user.js";
import {
  type LoadWorkflowPreviewDetailsQuery,
  useLoadWorkflowPreviewDetailsQuery,
} from "@/modules/workflow/graphql/workflow.gql.ts";
import { WorkflowContext } from "@/modules/workflow/stores/workflow-context.ts";

export type WorkflowData = LoadWorkflowPreviewDetailsQuery["workflow"]["findWorkflowById"];
export interface WorkflowDetailsCardProps extends GridCard {
  workflowId?: number | null;
  onLoad?: (workflow: WorkflowData) => void;
  title?: string;
}

export const WorkflowDetailsCard: FC<WorkflowDetailsCardProps> = (props: WorkflowDetailsCardProps) => {
  const { workflowId } = props;
  const { setLoading } = useContext(WorkflowContext);

  const { data, loading, error } = useLoadWorkflowPreviewDetailsQuery({ variables: { workflowId }, skip: !workflowId });
  void useError(error);

  const workflow = data?.workflow.findWorkflowById;
  const isVisuallyLoading = useVisuallyLoading(loading, 0, 300);

  useEffect(() => {
    setLoading(isVisuallyLoading);
  }, [isVisuallyLoading, setLoading]);

  useEffect(() => {
    if (data?.workflow.findWorkflowById) props.onLoad?.(data.workflow.findWorkflowById);
  }, [data, props.onLoad]);

  useEffect(
    () => () => {
      props.onLoad?.(null);
    },
    [props.onLoad],
  );

  if (!workflowId) {
    return null;
  }

  return (
    <Card title={props.title ?? "Information"} isLoading={isVisuallyLoading} data-testid="workflow-details-card">
      {!!workflow && (
        <>
          <CardRowSimple label="Name" value={workflow.name} />
          <CardRowSimple label="Description" value={workflow.description} />
          <CardRowSimple label="Date created" value={formatDateTimeFromISO(workflow.created)} />
          <CardRowSimple label="Created by" value={getUserDisplayName(workflow.creator as unknown as User)} />
          <CardRowSimple
            label="Visibility"
            value={workflow.usergroup.isPrivate ? "Private Workspace" : workflow.usergroup.name}
          />
        </>
      )}
    </Card>
  );
};
